/**
 * SetUpBimSystemsModal — "Set Up BIM Model": instantiate one or more BIM Systems
 * (+ their full element checklist) onto a project from the template library.
 *
 * Opens via CustomEvent('open-setup-bim-modal', { detail: { projectId, existingSystemTemplateIds } }).
 * existingSystemTemplateIds is the project's current systems' system_template_id list (may
 * repeat — a project can legitimately have more than one system of the same type, e.g. a
 * main house + an ADU) — shown as a count chip, never used to block re-adding.
 *
 * Only system templates (and, within a chosen system, element templates) marked
 * "Ready for use" in Admin → Templates → BIM are offered — drafts never land on a live
 * project by accident. No per-element picking: choosing a system adds its whole checklist,
 * mirroring how Drawing Sheet / WBS templates scaffold in this app.
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { getBimTemplates, addBimSystems } from '../services/bim.js'

const labelStyle = {
  display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10,
  color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase',
}
const helpStyle = {
  fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.45,
}
const inputStyle = {
  width: '100%', padding: '6px 9px', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5,
  color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
  borderRadius: 4, boxSizing: 'border-box', outline: 'none',
}

export default function SetUpBimSystemsModal() {
  const [open, setOpen] = useState(false)
  const [projectId, setProjectId] = useState('')
  const [existingIds, setExistingIds] = useState([])
  const [templates, setTemplates] = useState(null)   // ready system templates, or [] / null (loading)
  const [selected, setSelected] = useState({})       // { [templateId]: true }
  const [names, setNames] = useState({})             // { [templateId]: overrideName }
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const pid = e.detail?.projectId || ''
      setProjectId(pid)
      setExistingIds(e.detail?.existingSystemTemplateIds || [])
      setSelected({}); setNames({}); setError(null); setSubmitting(false)
      setTemplates(null)
      setOpen(true)
      getBimTemplates()
        .then(all => setTemplates((all || []).filter(t => t.ready_for_use)))
        .catch(() => setTemplates([]))
    }
    window.addEventListener('open-setup-bim-modal', onOpen)
    return () => window.removeEventListener('open-setup-bim-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function toggle(id) { setSelected(s => ({ ...s, [id]: !s[id] })) }

  const selectedIds = Object.keys(selected).filter(id => selected[id])

  async function handleSubmit() {
    if (!projectId || !selectedIds.length || submitting) return
    setSubmitting(true); setError(null)
    try {
      const items = selectedIds.map(id => {
        const override = (names[id] || '').trim()
        return { systemTemplateId: id, name: override || undefined }
      })
      const created = await addBimSystems(projectId, items)
      if (Array.isArray(ctx.bimCache)) ctx.bimCache = [...ctx.bimCache, ...created]
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
      window.dispatchEvent(new CustomEvent('bim-systems-added', { detail: { projectId, systems: created } }))
    } catch (ex) {
      setError(ex.message || 'Failed to add BIM systems'); setSubmitting(false)
    }
  }

  if (!open) return null
  const canSubmit = selectedIds.length > 0 && !submitting

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="SetUpBimSystemsModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 520, maxHeight: '82vh', display: 'flex', flexDirection: 'column', padding: '20px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, flexShrink: 0 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>Set Up BIM Systems</span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>
        <div style={{ ...helpStyle, flexShrink: 0, marginTop: 6 }}>
          Pick the systems this project needs to coordinate. Each one is added with its full
          element checklist from the template library — nothing to configure per element.
        </div>

        <div style={{ overflowY: 'auto', marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minHeight: 0 }}>
          {templates === null && <div style={helpStyle}>Loading templates…</div>}
          {templates && !templates.length && (
            <div style={helpStyle}>No system templates are marked "Ready for use" yet — check Admin → Templates → BIM.</div>
          )}
          {templates && templates.map(t => {
            const elCount = (t.elements || []).filter(e => e.ready_for_use).length
            const existingCount = existingIds.filter(id => id === t.id).length
            const isSel = !!selected[t.id]
            return (
              <div key={t.id} style={{ border: '0.5px solid var(--aj-border)', borderRadius: 6, padding: '8px 10px', background: isSel ? 'var(--aj-bg-subtle)' : 'none' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={isSel} onChange={() => toggle(t.id)}
                    style={{ marginTop: 2, accentColor: '#1D9E75', cursor: 'pointer', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' }}>{t.name}</span>
                      <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: 'var(--aj-text-muted)' }}>
                        {elCount} element{elCount === 1 ? '' : 's'}
                      </span>
                      {existingCount > 0 && (
                        <span style={{
                          fontFamily: '"DM Mono",monospace', fontSize: 9, textTransform: 'uppercase',
                          color: 'var(--aj-text-mid)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, padding: '1px 7px',
                        }}>Already on project ×{existingCount}</span>
                      )}
                    </div>
                    {isSel && (
                      <div style={{ marginTop: 6 }}>
                        <label style={{ ...labelStyle, marginBottom: 3 }}>Name (optional override)</label>
                        <input
                          style={inputStyle} value={names[t.id] ?? ''} placeholder={t.name}
                          onChange={e => setNames(n => ({ ...n, [t.id]: e.target.value }))}
                          onClick={e => e.stopPropagation()}
                        />
                      </div>
                    )}
                  </div>
                </label>
              </div>
            )
          })}
        </div>

        {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b', marginTop: 10, flexShrink: 0 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 14, flexShrink: 0 }}>
          <span style={helpStyle}>{selectedIds.length ? selectedIds.length + ' selected' : ''}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default' }}>
              {submitting ? 'Adding…' : 'Add ' + (selectedIds.length || '') + ' System' + (selectedIds.length === 1 ? '' : 's')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
